// Pure helpers for the glance components: projection, bounds, schedule
// lookups. No React here, so the maths can be read (and tested) alone.

export const CATEGORY_FILL = {
  off: "var(--off)", meet: "var(--meet)", sup: "var(--sup)", circ: "var(--circ)", tel: "var(--system)",
};
export const CATEGORY_LABEL = {
  off: "Offices", meet: "Meeting and phone rooms", sup: "Support spaces", circ: "Lobby, corridors, stairs",
};
export const DAY_LABEL = { weekday: "Weekday", saturday: "Saturday", sunday: "Sunday" };

export const sortedLevels = (m) => (m?.levels || []).slice().sort((a, b) => a.order - b.order);

export function centroid(pts) {
  let x = 0; let y = 0;
  pts.forEach((p) => { x += p[0]; y += p[1]; });
  return [x / pts.length, y / pts.length];
}

export function bounds(levels) {
  let xmin = Infinity; let ymin = Infinity; let xmax = -Infinity; let ymax = -Infinity;
  const eat = (p) => { xmin = Math.min(xmin, p[0]); xmax = Math.max(xmax, p[0]); ymin = Math.min(ymin, p[1]); ymax = Math.max(ymax, p[1]); };
  levels.forEach((l) => { (l.footprint || []).forEach(eat); (l.rooms || []).forEach((r) => r.polygon.forEach(eat)); });
  if (!Number.isFinite(xmin)) return { xmin: 0, ymin: 0, xmax: 1, ymax: 1, w: 1, h: 1 };
  return { xmin, ymin, xmax, ymax, w: Math.max(1, xmax - xmin), h: Math.max(1, ymax - ymin) };
}

// Oblique view: plan Y pushes up and slightly right, each level lifts by a
// fixed gap. Sized so the widest plate fills `width`.
export function makeStackProjector(b, plates, { width = 1140, depth = 0.42, top = 96, bottom = 40 } = {}) {
  const scale = (width - 80) / (b.w + b.h * depth * 0.55);
  const plate = b.h * scale * depth;
  // Guide 23.2 (building stack): scale fits the width, gap = plate depth plus
  // label room - RT06 comes out at scale 2.95, gap 210.
  const gap = plate + 64;
  const height = Math.ceil(top + (plates - 1) * gap + plate + bottom);
  const baseY = height - bottom;
  const project = (x, y, rank) => [
    40 + (x - b.xmin) * scale + (y - b.ymin) * scale * depth * 0.55,
    baseY - (rank - 1) * gap - (y - b.ymin) * scale * depth,
  ];
  return { project, height, width, scale };
}

export const pointsAttr = (pts, fn) => pts.map((p) => fn(p).map((v) => v.toFixed(1)).join(",")).join(" ");

// Profile value at an hour: 24-value arrays are percentages or on/off
// flags; [on, off] pairs are occupied hour ranges; null is "never".
export function profileAt(operations, name, dayType, hour) {
  if (name === "always") return 1;
  const prof = operations?.profiles?.[name];
  if (!prof) return null;
  const v = prof[dayType];
  if (v == null) return 0;
  if (v.length === 2) return hour >= v[0] && hour <= v[1] ? 1 : 0;
  return v[hour];
}

export const fmtHour = (h) => `${h % 12 || 12}:00 ${h < 12 ? "AM" : "PM"}`;

export function roomLabel(room, system) {
  return (system?.labels && system.labels[room.id]) || [room.name, room.number].filter(Boolean).join(" ") || "Unlabeled space";
}

// Notes by their group, in the order the groups first appear; notes with no
// group last (they are "What else the documents tell us").
export function noteGroups(notes) {
  const groups = new Map();
  (notes || []).forEach((n) => {
    const g = (n && n.group) || "";
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(n);
  });
  return [...groups.entries()].sort((a, b) => (a[0] === "") - (b[0] === "")).map(([group, list]) => ({ group, notes: list }));
}

export function sourceText(source) {
  if (!Array.isArray(source) || !source.length) return "";
  // A document is named by its file name when the source carries it (a bare id means nothing to a reader).
  return source.map((s) => [s.name || s.docId, s.sheet, s.page != null ? `p. ${s.page}` : ""].filter(Boolean).join(" ")).join("; ");
}

const DAY_MS = 24 * 3600 * 1000;
// Dates arrive as "YYYY-MM-DD" (or longer ISO) strings from the manifest,
// and as Date / epoch values from the axis ticks.
export const toTime = (d) => (typeof d === "string" ? new Date(`${d.slice(0, 10)}T00:00:00Z`).getTime() : new Date(d).getTime());

export function timelineRange(timeline, today) {
  if (timeline?.start && timeline?.end) return [toTime(timeline.start), toTime(timeline.end)];
  const ts = [];
  (timeline?.lanes || []).forEach((l) => {
    (l.items || []).forEach((i) => ts.push(toTime(i.date)));
    (l.spans || []).forEach((s) => { ts.push(toTime(s.start)); ts.push(toTime(s.end)); });
  });
  if (today) ts.push(toTime(today));
  if (!ts.length) return null;
  const lo = Math.min(...ts); const hi = Math.max(...ts);
  const pad = Math.max(10 * DAY_MS, (hi - lo) * 0.04);
  return [lo - pad, hi + pad];
}

// ---- all systems, zones and progress (guide sections 9 and 13) -------------

export const ROOF = "roof";
export const bucketKey = (level, system, zone) => `${level}|${system}|${zone}`;

// Systems drawn as geometry carry a height band (`tier`); the rest (a
// rooms-only highlight) are drawn by the room fills.
export const drawnSystems = (m) => (m?.systems || []).filter((s) => s.tier != null);
export const systemColor = (s) => `var(--${(s && s.token) || "system"})`;
export const hasRoofLevel = (m) => drawnSystems(m).some((s) => s.id === ROOF) || Object.keys(m?.buckets || {}).some((k) => k.startsWith(`${ROOF}|`));

export function isCovered(system, level, zone) {
  const cov = system && system.coverage;
  if (!cov || !cov.levels) return true;
  const v = cov.levels[level];
  return v === "all" || (Array.isArray(v) && v.includes(zone));
}

export function bucketCount(b) {
  if (!b) return 0;
  return ["lines", "polys", "dots", "marks"].reduce((n, k) => n + ((b[k] || []).length), 0);
}

// One SVG path string per kind of shape in a bucket. `proj(x, y)` returns
// screen coordinates, so the same bucket draws on the stack, the zone view
// and the plan.
const f1 = (v) => v.toFixed(1);
export function linesPath(lines, proj) {
  let d = "";
  (lines || []).forEach((g) => { const a = proj(g[0], g[1]); const c = proj(g[2], g[3]); d += `M${f1(a[0])} ${f1(a[1])}L${f1(c[0])} ${f1(c[1])}`; });
  return d;
}
export function polysPath(polys, proj) {
  let d = "";
  (polys || []).forEach((poly) => { poly.forEach((q, i) => { const a = proj(q[0], q[1]); d += `${i ? "L" : "M"}${f1(a[0])} ${f1(a[1])}`; }); d += "Z"; });
  return d;
}
export function dotsPath(dots, proj, r) {
  let d = "";
  (dots || []).forEach((q) => { const a = proj(q[0], q[1]); d += `M${f1(a[0] - r)} ${f1(a[1] - r * 0.5)}h${2 * r}v${r}h${-2 * r}Z`; });
  return d;
}
export function marksPath(marks, proj, r) {
  let d = "";
  (marks || []).forEach((q) => { const a = proj(q[0], q[1]); d += `M${f1(a[0])} ${f1(a[1] - r)}l${r} ${r}l${-r} ${r}l${-r} ${-r}Z`; });
  return d;
}

export const STATUS_COLOR = { todo: "var(--todo)", prog: "var(--riser)", done: "var(--done)", blocked: "var(--block)" };
export const STATUS_NAME = { todo: "Not started", prog: "In progress", done: "Complete", blocked: "Blocked" };

// The same rules as back/utils/glance/progress.statusAt, kept in step.
const DAY_MS_ = 24 * 3600 * 1000;
export function statusAt(a, date, today) {
  const t = (v) => (v ? +new Date(v) : null);
  const d = t(date); const now = t(today) || Date.now();
  const plannedStart = t(a.start) || t(a.end); const plannedEnd = t(a.end);
  const reportsWork = a.status === "in_progress" || a.status === "done" || (a.progress || 0) > 0;
  const started = t(a.actualStart) || (reportsWork ? Math.min(plannedStart, now) : null);
  const finished = t(a.actualEnd) || (a.status === "done" || (a.progress || 0) >= 100 ? Math.min(plannedEnd, now) : null);
  const linear = (s, e) => (e <= s ? (d >= e ? 100 : 0) : Math.round(Math.max(0, Math.min(1, (d - s) / (e - s))) * 100));
  if (finished && d >= finished) return { st: "done", pct: 100 };
  if (d <= now + DAY_MS_ / 2) {
    if (!started || d < started) return { st: "todo", pct: 0 };
    const pct = Math.min(99, d >= now - DAY_MS_ ? (a.progress || 0) : Math.min(a.progress || 0, linear(started, plannedEnd)));
    return { st: a.openIssues ? "blocked" : "prog", pct };
  }
  if (plannedEnd < now) return { st: "prog", pct: Math.min(99, a.progress || 0) };
  if (d >= plannedEnd) return { st: "done", pct: 100 };
  if (d >= plannedStart) return { st: "prog", pct: Math.max(a.progress || 0, linear(plannedStart, plannedEnd)) };
  return { st: "todo", pct: 0 };
}
export function rollupStatus(statuses) {
  if (!statuses.length) return null;
  const pct = Math.round(statuses.reduce((n, s) => n + s.pct, 0) / statuses.length);
  if (statuses.some((s) => s.st === "blocked")) return { st: "blocked", pct };
  if (statuses.every((s) => s.st === "done")) return { st: "done", pct: 100 };
  if (statuses.every((s) => s.st === "todo")) return { st: "todo", pct: 0 };
  return { st: "prog", pct };
}

// key -> { st, pct, activities: [...], field? } for a progress payload on a date.
//
// Today and after, a bucket the field has reported on (published technical
// views, guide 9.8 / 19.5) takes its status from what was installed, not from
// the schedule's dates; the activities stay beside it to explain it. Dates in
// the past still read the schedule, because the field's history is not
// replayed here.
export function statusByKey(progress, date) {
  const out = {};
  if (!progress) return out;
  Object.entries(progress.keys || {}).forEach(([key, ids]) => {
    const acts = ids.map((id) => progress.activities[id]).filter(Boolean);
    const r = rollupStatus(acts.map((a) => statusAt(a, date, progress.today)));
    if (r) out[key] = { ...r, activities: acts };
  });
  const zones = progress.field && progress.field.zones;
  if (zones && (!date || !progress.today || String(date).slice(0, 10) >= String(progress.today).slice(0, 10))) {
    Object.entries(zones).forEach(([key, z]) => {
      out[key] = { st: z.status, pct: z.percent, activities: (out[key] && out[key].activities) || [], field: z };
    });
  }
  return out;
}

// The zone page's shareable address: #level=L1&zone=3&sys=telecom,power
// A size filter is never a default (owner, 2026-09-18: "2\" filter should only
// be obtained by new views"): it arrives only from a technical view, as
// &size=2"&view=<the view's name>, and &only=0 when the person switched it off.
// An old "&two=1" address is read as no size.
export function parseZoneHash(hash) {
  const h = {};
  String(hash || "").replace(/^#/, "").split("&").filter(Boolean).forEach((kv) => {
    const [k, v = ""] = kv.split("=");
    h[decodeURIComponent(k)] = decodeURIComponent(v);
  });
  return {
    level: h.level || null,
    zone: h.zone != null && h.zone !== "" && !Number.isNaN(Number(h.zone)) ? Number(h.zone) : null,
    sys: h.sys != null ? h.sys.split(",").filter(Boolean) : null,
    size: h.size || null,
    view: h.size ? (h.view || "") : "",
    only: h.size ? h.only !== "0" : false,
  };
}
export const zoneHash = ({ level, zone, sys, size, view, only }) =>
  `#level=${encodeURIComponent(level)}&zone=${zone}${sys ? `&sys=${sys.map(encodeURIComponent).join(",")}` : ""}`
  + (size ? `&size=${encodeURIComponent(size)}&view=${encodeURIComponent(view || "")}${only === false ? "&only=0" : ""}` : "");

// Whether an item is of a size a view asked for ("2\"" must not match 12" or
// 1-1/2"). Items that carry no size (a light fixture) are not judged here.
export function sizeMatch(item, size) {
  if (!size || !item) return false;
  const want = String(size).replace(/\s+/g, "");
  if (want === '2"' && item.two) return true;
  if (item.size && String(item.size).replace(/\s+/g, "") === want) return true;
  const esc = want.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^\\d/-])${esc}`).test(`${item.t || ""} ${item.desc || ""}`.replace(/\s+"/g, '"'));
}
export const carriesSize = (item) => !!item && (item.c === "Keynoted pathways" || !!item.size);

export const zonePageUrl = (projectId, state) => `/coordination/${projectId}/zone${zoneHash(state)}`;

// The overview's own state in its URL hash (guide 20.6 / 23): which mode,
// which systems, the hour and day of "A day in the building", the progress
// date and the level of the floor plans - so a link opens the same picture.
export function parseOverviewHash(hash) {
  const h = {};
  String(hash || "").replace(/^#/, "").split("&").filter(Boolean).forEach((kv) => {
    const [k, v = ""] = kv.split("=");
    h[decodeURIComponent(k)] = decodeURIComponent(v);
  });
  const hour = h.hour != null && /^\d{1,2}$/.test(h.hour) && Number(h.hour) <= 23 ? Number(h.hour) : null;
  return {
    mode: ["systems", "day", "progress"].includes(h.mode) ? h.mode : null,
    sys: h.sys != null ? h.sys.split(",").filter(Boolean) : null,
    hour,
    day: h.day || null,
    date: /^\d{4}-\d{2}-\d{2}$/.test(h.date || "") ? h.date : null,
    level: h.level || null,
    // A zone pinned open (by a tap or the arrow keys - not a passing hover): "L2:3".
    zone: /^[^:]+:\d+$/.test(h.zone || "") ? { level: h.zone.split(":")[0], zone: Number(h.zone.split(":")[1]) } : null,
  };
}
export function overviewHash({ mode, sys, hour, day, date, level, zone }) {
  const parts = [];
  if (mode && mode !== "systems") parts.push(`mode=${mode}`);
  if (sys) parts.push(`sys=${sys.map(encodeURIComponent).join(",")}`);
  if (mode === "day") { if (hour != null) parts.push(`hour=${hour}`); if (day) parts.push(`day=${encodeURIComponent(day)}`); }
  if (mode === "progress" && date) parts.push(`date=${date}`);
  if (level) parts.push(`level=${encodeURIComponent(level)}`);
  if (zone && zone.level && Number.isInteger(zone.zone)) parts.push(`zone=${encodeURIComponent(zone.level)}:${zone.zone}`);
  return parts.length ? `#${parts.join("&")}` : "";
}

// Arrow-key stepping between zones (left/right) and levels (up/down) while a
// zone is open. Returns the next { level, zone }, or null at the edge.
export function stepZone({ level, zone }, key, levelIds, zoneIdx) {
  const li = levelIds.indexOf(level); const zi = zoneIdx.indexOf(zone);
  if (li < 0 || zi < 0) return null;
  if (key === "ArrowLeft" && zi > 0) return { level, zone: zoneIdx[zi - 1] };
  if (key === "ArrowRight" && zi < zoneIdx.length - 1) return { level, zone: zoneIdx[zi + 1] };
  if (key === "ArrowUp" && li < levelIds.length - 1) return { level: levelIds[li + 1], zone };
  if (key === "ArrowDown" && li > 0) return { level: levelIds[li - 1], zone };
  return null;
}

export const levelLabel = (m, id) => (id === ROOF ? "Roof" : ((m?.levels || []).find((l) => l.id === id) || {}).name || id);



// Guide 20.3, the short routes: open the New view dialog on the Technical
// documentation page, pre-filled from what the person is looking at.
// prefill = { title, request: { systems, levels, zones, filter, elementKeys, include } }.
// It travels in the history state: a selection can be thousands of keys.
export function openNewView(history, projectId, prefill) {
  history.push(`/documentation/${projectId}?tab=views&new=1`, { newViewPrefill: prefill || null });
}

// Guide 23.2: one plan projector for every top-down drawing (the overview's
// plan, the zone page's plan, a tracking sheet's plan viewport): building
// feet to the drawing, orthographic, y up. Each caller keeps its own scale and
// margins; the arithmetic - and its inverse, for pointing (measure, mark) - is
// here once.
export function orthoPlan({ xmin, ymin, h }, { scale, left = 30, bottom = 30, top = 30 }) {
  const height = Math.ceil(h * scale + top + bottom);
  const P = (x, y) => [left + (x - xmin) * scale, height - bottom - (y - ymin) * scale];
  const inverse = (sx, sy) => [xmin + (sx - left) / scale, ymin + (height - bottom - sy) / scale];
  return { P, inverse, height, scale };
}

// 10.42 ft -> 10'-5" (to the nearest inch).
export function ftInText(ft) {
  const inches = Math.round(Math.abs(Number(ft) || 0) * 12);
  return `${Math.floor(inches / 12)}'-${inches % 12}"`;
}

// The ceiling heights the documents give for rooms in a zone (guide 9.1/9.5):
// "9'-10" to 10'-5" (12 of 40 rooms)", or "" when no room carries one.
export function zoneCeilings(level, zone) {
  if (!level || !zone) return "";
  const inZone = (level.rooms || []).filter((r) => {
    const xs = (r.polygon || []).map((p) => p[0]);
    if (!xs.length) return false;
    const cx = xs.reduce((a, b) => a + b, 0) / xs.length;
    return cx >= zone.x0 && cx < zone.x1;
  });
  const hs = inZone.map((r) => r.ceilingFt).filter((h) => h != null);
  if (!hs.length) return "";
  const lo = Math.min(...hs); const hi = Math.max(...hs);
  return `${lo === hi ? ftInText(lo) : `${ftInText(lo)} to ${ftInText(hi)}`} (${hs.length} of ${inZone.length} rooms)`;
}
